import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { ShieldOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuthStore } from '@/services/store/use-auth-store.tsx';
import { usePostReset2FAMutation } from '@/features/auth/hooks/mutations/use-post-reset-2fa.ts';
import { StatusAlert } from '../status-alert';

const getTwoFactorResetSchema = (translation?: {
  emailRequired: string;
  emailValid: string;
}) => {
  const messages = translation ?? {
    emailRequired: 'Email is required',
    emailValid: 'Please enter a valid email',
  };

  return z.object({
    email: z
      .string({
        error: messages.emailRequired,
      })
      .min(1, { message: messages.emailRequired })
      .email({
        message: messages.emailValid,
      }),
  });
};

export type TwoFactorResetFormSchema = z.infer<ReturnType<typeof getTwoFactorResetSchema>>;

export const TwoFactorResetForm = () => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const [statusMessage, setStatusMessage] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const { isAuthenticated } = useAuthStore();

  const twoFactorResetSchema = getTwoFactorResetSchema({
    emailValid: t('auth.errors.emailValid'),
    emailRequired: t('auth.errors.emailRequired'),
  });

  const form = useForm({
    resolver: zodResolver(twoFactorResetSchema),
    defaultValues: {
      email: '',
    },
  });

  const { mutate: twoFactorResetMutation, isPending } = usePostReset2FAMutation({
    onSuccess: () => {
      form.reset();
      setStatusMessage({
        type: 'success',
        message: t('auth.twoFactorReset.successMessage'),
      });
    },
    onError: () => {
      setStatusMessage({
        type: 'success',
        message: '',
      });
    },
  });

  async function onSubmit({ email }: TwoFactorResetFormSchema) {
    twoFactorResetMutation({ email });
  }

  if (!['2FA_VERIFY_PAGE'].includes(state?.from)) {
    return (
      <div className="min-h-[390px]">
        <Navigate to="/" replace={true} />
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div className="min-h-[390px]">
        <Navigate to="/" replace={true} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md">
      <div className="flex max-w-[400px] flex-col gap-4">
        {statusMessage && (
          <div className="flex w-full items-center justify-center">
            <StatusAlert
              type={statusMessage.type}
              message={statusMessage.message}
              className="max-w-[300px]"
            />
          </div>
        )}

        <div className="space-y-2 text-center">
          <div className="flex justify-center">
            <ShieldOff size="52" className="text-[#383E42]" />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900">
            {t('auth.twoFactorReset.title')}
          </h2>
          <p className="text-sm text-gray-600">{t('auth.twoFactorReset.description')}</p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">
                    {t('auth.twoFactorReset.emailLabel')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      autoComplete="email"
                      placeholder={t('auth.twoFactorReset.emailPlaceholder')}
                      disabled={isPending}
                      className="focus:border-primary focus:ring-primary h-10 border-gray-200 bg-white text-gray-900 placeholder:text-gray-400"
                    />
                  </FormControl>
                  <FormMessage className="text-sm" />
                </FormItem>
              )}
            />

            <Button
              disabled={isPending}
              type="submit"
              className="bg-primary h-10 w-full font-medium shadow-sm transition-all hover:shadow-md disabled:opacity-50"
            >
              {isPending ? (
                <LoadingSpinner className="h-5 w-5" />
              ) : (
                t('auth.twoFactorReset.requestButton')
              )}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};
